import { getDemoData, matchRestRequest } from '../utils'
import { DemoApiMock } from '../types'
import { parse } from 'date-fns'

interface AssignmentDetailRequest {
  assignmentId: number
  startDateDay?: string
  endDateDay?: string
}

export const mockGetAssignment: DemoApiMock = {
  shouldMock: (request) =>
    matchRestRequest(
      request,
      '/company/graphql_dispatch/assignment_detail_b60f4459',
      'POST'
    ) ||
    matchRestRequest(
      request,
      '/company/graphql_dispatch/assignment_shifts_94cc266e',
      'POST'
    ) ||
    matchRestRequest(
      request,
      '/company/graphql_dispatch/assignment_calendar_view_41b5c554',
      'POST'
    ),
  getResponse: async (request) => {
    const params = request.data as AssignmentDetailRequest
    const assignmentId = params.assignmentId
    const demoData = getDemoData()
    const assignment = demoData.assignments?.find((a) => a.id === assignmentId)
    const shifts = demoData.shifts.filter(
      (s) => s.assignment?.id === assignmentId
    )
    if (!assignment) {
      throw new Error(`Assignment with id ${assignmentId} not found`)
    }

    // calendar events query
    if (params.startDateDay) {
      const startDate = parse(params.startDateDay, 'yyyyMMdd', new Date())
      const endDate = params.endDateDay
        ? parse(params.endDateDay, 'yyyyMMdd', new Date())
        : undefined
      const filteredShifts = shifts.filter((shift) => {
        const date = new Date(shift.startsAt!)
        return (
          (!startDate || date >= startDate) && (!endDate || date <= endDate)
        )
      })
      return {
        data: {
          company: {
            assignment: {
              id: assignment.id,
              name: assignment.name,
              shifts: filteredShifts,
              workers: Array.from(
                new Map(
                  filteredShifts
                    .filter((shift) => shift.assignment?.id === assignmentId)
                    .flatMap((shift) =>
                      (shift.work ?? []).map((w) => ({
                        id: w.worker?.id,
                        name: w.worker?.name,
                        profilePicUrl: w.worker?.profilePicUrl,
                        workForAssignment: [],
                      }))
                    )
                    .filter((w) => w.id) // Remove entries without IDs
                    .map((w) => [w.id, w]) // Create entries for Map
                ).values()
              ),
              listings: [
                {
                  id: assignment.id,
                  numWorkersPending: Math.max(
                    ...filteredShifts
                      .filter((shift) => !shift.cancelledAt)
                      .map((shift) => shift.numOpenSpots ?? 0)
                  ),
                  shifts: filteredShifts.map((shift) => ({
                    id: shift.id,
                    numOpenSpots: shift.numOpenSpots ?? 0,
                  })),
                },
              ],
            },
          },
        },
      }
    }

    // assignment shifts query
    const requestURL = request.url || ''
    if (
      requestURL.match('/company/graphql_dispatch/assignment_shifts_94cc266e')
    ) {
      return {
        data: {
          company: {
            assignment: {
              name: assignment.name,
              position: { name: assignment.position?.name },
              shifts: shifts.map((s) => ({
                id: s.id,
                startsAt: s.startsAt,
                endsAt: s.endsAt,
                workersNeeded: s.workersNeeded,
                numWorkersFilled: s.numWorkersFilled || 0,
                isOrientation: s.isOrientation,
                location: {
                  id: s.location?.id,
                  name: s.location?.name,
                  address: {
                    city: s.location?.address?.city,
                    state: s.location?.address?.state,
                    timezone: s.location?.address?.timezone,
                  },
                },
                position: { name: s.position?.name },
              })),
            },
          },
        },
      }
    }

    // assignment detail query
    return {
      data: {
        company: {
          assignment: {
            id: assignment.id,
            name: assignment.name,
            hasOrientation: assignment.hasOrientation,
          },
        },
      },
    }
  },
}
